import { useState, useRef, useId } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Mail, Send, Loader2, CheckCircle2, AlertCircle, Clock, Globe } from 'lucide-react';
import { collectClientContext } from '../lib/clientContext';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

// Subjects MUST match the ALLOWED_SUBJECTS set in api/routes/contact.js.
// Keep this list in lockstep with the backend.
const SUBJECTS = [
  { value: 'General inquiry', i18nKey: 'subject_general' },
  { value: 'Bug report', i18nKey: 'subject_bug' },
  { value: 'Feature request', i18nKey: 'subject_feature' },
  { value: 'Billing question', i18nKey: 'subject_billing' },
  { value: 'Partnership', i18nKey: 'subject_partnership' },
  { value: 'Press', i18nKey: 'subject_press' },
];

const MAX_NAME = 120;
const MAX_EMAIL = 254;
const MAX_MESSAGE = 5000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const honeypotRef = useRef(null);

  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [ticketId, setTicketId] = useState(null);

  const errorRegionId = useId();

  function validate() {
    const errs = {};
    if (!name.trim()) errs.name = t('marketing:contact.error_required', 'This field is required.');
    if (!email.trim()) errs.email = t('marketing:contact.error_required', 'This field is required.');
    else if (!EMAIL_RE.test(email.trim())) errs.email = t('marketing:contact.error_email', 'Enter a valid email address.');
    if (!subject) errs.subject = t('marketing:contact.error_subject', 'Please choose a subject.');
    if (!message.trim()) errs.message = t('marketing:contact.error_required', 'This field is required.');
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMessage(null);

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      // Move focus to first invalid field for screen-reader users.
      const first = Object.keys(errs)[0];
      const el = document.getElementById(`contact-${first}`);
      if (el) el.focus();
      return;
    }
    setFieldErrors({});

    setStatus('sending');
    try {
      const client = await collectClientContext();
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: message.trim(),
          appVersion: client.appVersion || undefined,
          platform: client.platform || undefined,
          _hp: honeypotRef.current?.value || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success !== true) {
        throw new Error(data.message || t('marketing:contact.error_generic', 'Sorry, something went wrong. Please try again.'));
      }
      setTicketId(data.ticketId || null);
      setStatus('success');
      setName(''); setEmail(''); setSubject(''); setMessage('');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message);
    }
  }

  return (
    <>
      <Helmet>
        <title>{t('marketing:contact.meta_title')}</title>
        <meta name="description" content={t('marketing:contact.hero_sub')} />
        <link rel="canonical" href="https://scrivar.com/contact" />
      </Helmet>

      <div className="bg-gray-50">
        <div>
          {/* Hero */}
          <section className="bg-white border-b border-gray-100">
            <div className="max-w-4xl mx-auto px-6 py-14 sm:py-20 text-center">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
                {t('marketing:contact.hero_heading')}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
                {t('marketing:contact.hero_sub')}
              </p>
            </div>
          </section>

          {/* Form + sidebar */}
          <section className="max-w-6xl mx-auto px-6 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                {status === 'success' ? (
                  <SuccessCard
                    heading={t('marketing:contact.success_heading')}
                    body={t('marketing:contact.success_body')}
                    ticketId={ticketId}
                    onSendAnother={() => { setStatus('idle'); setTicketId(null); }}
                  />
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    noValidate
                    className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8"
                  >
                    {/* Honeypot — hidden from sight + screen readers, visible to bots */}
                    <div
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                    >
                      <label>
                        Leave this field blank
                        <input
                          ref={honeypotRef}
                          type="text"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field
                        id="contact-name"
                        label={t('marketing:contact.field_name')}
                        required
                        error={fieldErrors.name}
                      >
                        <input
                          id="contact-name"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          maxLength={MAX_NAME}
                          autoComplete="name"
                          required
                          aria-invalid={!!fieldErrors.name}
                          aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                          className={inputClass(!!fieldErrors.name)}
                        />
                      </Field>

                      <Field
                        id="contact-email"
                        label={t('marketing:contact.field_email')}
                        required
                        error={fieldErrors.email}
                      >
                        <input
                          id="contact-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          maxLength={MAX_EMAIL}
                          autoComplete="email"
                          required
                          aria-invalid={!!fieldErrors.email}
                          aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                          className={inputClass(!!fieldErrors.email)}
                        />
                      </Field>
                    </div>

                    <div className="mt-4">
                      <Field
                        id="contact-subject"
                        label={t('marketing:contact.field_subject')}
                        required
                        error={fieldErrors.subject}
                      >
                        <select
                          id="contact-subject"
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          required
                          aria-invalid={!!fieldErrors.subject}
                          aria-describedby={fieldErrors.subject ? 'contact-subject-error' : undefined}
                          className={inputClass(!!fieldErrors.subject)}
                        >
                          <option value="">{t('marketing:contact.subject_placeholder')}</option>
                          {SUBJECTS.map((s) => (
                            <option key={s.value} value={s.value}>
                              {t(`marketing:contact.${s.i18nKey}`)}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <div className="mt-4">
                      <Field
                        id="contact-message"
                        label={t('marketing:contact.field_message')}
                        required
                        error={fieldErrors.message}
                        hint={`${message.length} / ${MAX_MESSAGE}`}
                      >
                        <textarea
                          id="contact-message"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          maxLength={MAX_MESSAGE}
                          rows={6}
                          required
                          aria-invalid={!!fieldErrors.message}
                          aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                          className={`${inputClass(!!fieldErrors.message)} resize-y min-h-[140px]`}
                        />
                      </Field>
                    </div>

                    {status === 'error' && errorMessage && (
                      <div
                        id={errorRegionId}
                        role="alert"
                        className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800"
                      >
                        <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                        <span>{errorMessage}</span>
                      </div>
                    )}

                    <p className="mt-4 text-xs text-gray-500">
                      {t('marketing:contact.privacy_note')}
                    </p>

                    <div className="mt-6">
                      <button
                        type="submit"
                        disabled={status === 'sending'}
                        className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-white font-semibold shadow-sm transition-all hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
                        style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
                      >
                        {status === 'sending' ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            {t('common:buttons.sending')}
                          </>
                        ) : (
                          <>
                            <Send size={18} />
                            {t('marketing:contact.submit')}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Sidebar */}
              <aside className="space-y-4">
                <SidebarCard
                  icon={<Mail size={18} className="text-indigo-600" />}
                  label={t('marketing:contact.email_label')}
                >
                  <a
                    href="mailto:support@scrivar.com"
                    className="text-indigo-600 font-semibold hover:underline"
                  >
                    support@scrivar.com
                  </a>
                </SidebarCard>

                <SidebarCard
                  icon={<Clock size={18} className="text-indigo-600" />}
                  label={t('marketing:contact.response_label')}
                >
                  <p className="text-gray-700">{t('marketing:contact.response_time')}</p>
                </SidebarCard>

                <SidebarCard
                  icon={<Globe size={18} className="text-indigo-600" />}
                  label={t('marketing:contact.web_label')}
                >
                  <a href="/help" className="text-indigo-600 font-semibold hover:underline">
                    {t('marketing:contact.help_link', 'Browse the Help Center →')}
                  </a>
                </SidebarCard>

                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm text-gray-700 leading-relaxed">
                  <Trans
                    i18nKey="marketing:contact.billing_tip"
                    components={{
                      a: <a href="/account/billing" className="text-indigo-600 font-semibold hover:underline">account page →</a>,
                    }}
                  />
                </div>
              </aside>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function inputClass(hasError) {
  return [
    'w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm',
    'transition-shadow focus:outline-none focus:ring-2',
    hasError
      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
      : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-200',
  ].join(' ');
}

function Field({ id, label, required, error, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <label htmlFor={id} className="block text-sm font-medium text-gray-800">
          {label}
          {required && <span className="text-red-600 ml-0.5" aria-hidden="true">*</span>}
        </label>
        {hint && <span className="text-xs text-gray-400 tabular-nums">{hint}</span>}
      </div>
      {children}
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function SidebarCard({ icon, label, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          {label}
        </span>
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function SuccessCard({ heading, body, ticketId, onSendAnother }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center" role="status">
      <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle2 size={28} className="text-emerald-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900">{heading}</h2>
      <p className="mt-2 text-gray-600 leading-relaxed">{body}</p>
      {ticketId && (
        <p style={{ fontSize: 13, color: '#666', marginTop: 8 }}>
          Your ticket reference is <strong>#{String(ticketId).slice(0, 8)}</strong>. We&apos;ll reply by email, and you can follow it under Support when signed in.
        </p>
      )}
      <button
        type="button"
        onClick={onSendAnother}
        className="mt-6 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
      >
        Send another message
      </button>
    </div>
  );
}
